import { c as _c } from "react/compiler-runtime";
import { type AnchorHTMLAttributes, memo } from 'react';
import { LinkIt, emailRegex, urlRegex } from 'react-linkify-it';

// urlRegex extended to also stop at full-width punctuation (never valid in URLs)
const fullWidthPunctuation = '\u3000-\u303F\uFF00-\uFFEF';
const extendedUrlRegex = new RegExp(urlRegex.source.replaceAll('[^\\s', `[^\\s${fullWidthPunctuation}`), urlRegex.flags);
const invalidUrlChars = `\\s${fullWidthPunctuation}<>'"()[\\]{}`;
const trailingUrlPunctuation = '.,;:!?\\])}';
const bareDomainRegex = new RegExp(`(?<![@\\w-])(?:[a-z\\d](?:[a-z\\d-]{0,61}[a-z\\d])?\\.)+[a-z]{2,}(?:[/?#][^${invalidUrlChars}]*[^${invalidUrlChars}${trailingUrlPunctuation}])?`, 'iu');
const hasUrlScheme = (value: string) => /^[a-z][a-z\d+.-]*:\/\//i.test(value);
const normalizeHref = (value: string) => hasUrlScheme(value) ? value : `https://${value}`;
type TextWithLinksProps = {
  text: string;
  linkProps?: AnchorHTMLAttributes<HTMLAnchorElement>;
};
type LinkRendererProps = {
  match: string;
  linkProps?: AnchorHTMLAttributes<HTMLAnchorElement>;
};
function UrlLink(t0) {
  const $ = _c(6);
  const {
    match,
    linkProps
  } = t0;
  let t1;
  if ($[0] !== match) {
    t1 = normalizeHref(match);
    $[0] = match;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== linkProps || $[3] !== match || $[4] !== t1) {
    t2 = <a href={t1} target="_blank" rel="noopener noreferrer" {...linkProps}>{match}</a>;
    $[2] = linkProps;
    $[3] = match;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function EmailLink(t0) {
  const $ = _c(4);
  const {
    match,
    linkProps
  } = t0;
  const t1 = `mailto:${match}`;
  let t2;
  if ($[0] !== linkProps || $[1] !== match || $[2] !== t1) {
    t2 = <a href={t1} target="_blank" rel="noopener noreferrer" {...linkProps}>{match}</a>;
    $[0] = linkProps;
    $[1] = match;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
const TextWithLinks = memo(function TextWithLinks(t0) {
  const $ = _c(15);
  const {
    text,
    linkProps
  } = t0;
  let t1;
  if ($[0] !== linkProps) {
    t1 = (match, key) => <EmailLink key={key} match={match} linkProps={linkProps} />;
    $[0] = linkProps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== linkProps) {
    t2 = (match_0, key_0) => <UrlLink key={key_0} match={match_0} linkProps={linkProps} />;
    $[2] = linkProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== linkProps) {
    t3 = (match_1, key_1) => <UrlLink key={key_1} match={match_1} linkProps={linkProps} />;
    $[4] = linkProps;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3 || $[7] !== text) {
    t4 = <LinkIt regex={bareDomainRegex} component={t3}>{text}</LinkIt>;
    $[6] = t3;
    $[7] = text;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <LinkIt regex={extendedUrlRegex} component={t2}>{t4}</LinkIt>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t5) {
    t6 = <LinkIt regex={emailRegex} component={t1}>{t5}</LinkIt>;
    $[12] = t1;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
});
export default TextWithLinks;